import { EventType } from "@/analytics/analytics-events";
import {
  ClearScanSelectionProperties,
  SelectedScanProperties,
  ToggleScanVisibilityProperties,
  type ToggleFixedStatusForSelectedScans,
} from "@/analytics/staging-area-workflows-events-properties";
import { Features } from "@/store/features/features";
import { selectHasFeature } from "@/store/features/features-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import {
  ContextualToolbar,
  DeleteIcon,
  FaroIconButton,
  FaroTooltip,
  FullScreenIcon,
  NonVisibleIcon,
  ToolGroup,
  useToast,
  VisibleIcon,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID } from "@faro-lotv/foundation";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { useCallback, useMemo, useState, type JSX } from "react";
import { Box3 } from "three";
import { useSelectedEntitiesDescription } from "../../hooks/use-selected-entities-description";
import { useDataPreparationOverlayElements } from "../data-preparation-overlay-context";
import { useIsDataPreparationReadOnly } from "../hooks/use-is-data-preparation-read-only";
import { ReactComponent as ReleaseAllSelectedScansIcon } from "../icons/anchor-basic-striketrough.svg";
import { ReactComponent as FixAllSelectedScansIcon } from "../icons/anchor-basic.svg";
import { ReactComponent as SelectConnectedScansIcon } from "../icons/registration-direct-edges-pick.svg";
import {
  selectAllConnectedComponents,
  selectRevisionEntityWorldPositions,
  selectSelectedCapturesRecursive,
  selectSelectedClustersRecursive,
  selectSelectedEntityIds,
  selectSelectedEntityIdsForVisibility,
  selectSelectedScanIdsRecursive,
  selectVisibilityMap,
} from "../store/data-preparation-ui/data-preparation-ui-selectors";
import {
  setSelectedEntityIds,
  setVisibilityForEntities,
} from "../store/data-preparation-ui/data-preparation-ui-slice";
import { selectFixedRevisionEntityIds } from "../store/revision/revision-entity-selectors";
import { selectEditMode } from "../store/user-edits/data-preparation-user-edit-selectors";
import { normalizeSelection } from "../utils/normalize-selection";
import { DeleteEntitiesDialog } from "./data-preparation-dialogs/delete-entities-dialog";
import {
  computeFixedStatus,
  toggleFixedStatus,
} from "../utils/toggle-fixed-status";

/** @returns A toolbar to provide contextual actions on the selected scans in the revision. */
export function RevisionContextualToolbar(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const isReadOnly = useIsDataPreparationReadOnly();
  const { openToast } = useToast();
  const directlySelectedIds = useAppSelector(selectSelectedEntityIds);
  const isFixAndReleaseEnabled = useAppSelector(
    selectHasFeature(Features.FixAndRelease),
  );
  const isEditModeEnabled = useAppSelector(selectEditMode) !== undefined;
  const selectedEntityIdsForVisibility = useAppSelector(
    selectSelectedEntityIdsForVisibility,
  );
  const selectedClusters = useAppSelector(selectSelectedClustersRecursive);
  const selectedCaptures = useAppSelector(selectSelectedCapturesRecursive);
  const selectedScanIds = useAppSelector(selectSelectedScanIdsRecursive);

  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState(false);

  const visibleEntityCount = useVisibleCount(selectedEntityIdsForVisibility);
  const hasInvisibleEntities =
    visibleEntityCount !== selectedEntityIdsForVisibility.length;

  const entitiesSelectedDescription = useSelectedEntitiesDescription({
    scansSelectedCount: selectedCaptures.length,
    clustersSelectedCount: selectedClusters.length,
    separator: " • ",
    selectedPosition: "perSection",
  });

  return (
    <>
      {directlySelectedIds.length > 0 && (
        <ContextualToolbar
          label={entitiesSelectedDescription}
          clearSelection={() => {
            Analytics.track<ClearScanSelectionProperties>(
              EventType.clearScanSelection,
              {
                via: "context toolbar",
                selectedScanCount: selectedCaptures.length,
              },
            );
            dispatch(setSelectedEntityIds([]));
          }}
        >
          <ToolGroup orientation="horizontal" sx={{ p: 0.5 }}>
            <VisibilityToggle
              directlySelectedIds={directlySelectedIds}
              selectedEntityIds={selectedEntityIdsForVisibility}
            />
            <SelectAllConnectedScans selectedScanIds={selectedScanIds} />
            {isFixAndReleaseEnabled && isEditModeEnabled && (
              <ToggleFixedStatusForAllSelectedScans
                selectedScanIds={selectedScanIds}
                isReadOnly={isReadOnly}
              />
            )}
            <FocusOnSelectionButton selectedScanIds={selectedScanIds} />
            <FaroTooltip title="Delete selection">
              <FaroIconButton
                aria-label="delete selected items"
                disabled={isReadOnly}
                dark
                onClick={() => {
                  if (hasInvisibleEntities) {
                    openToast({
                      title: "Cannot delete items",
                      variant: "warning",
                      message:
                        "Deleting is not possible while hidden items are selected. Make them visible to delete.",
                    });
                  } else {
                    setIsDeletionDialogOpen(true);
                  }
                }}
              >
                <DeleteIcon />
              </FaroIconButton>
            </FaroTooltip>
          </ToolGroup>
        </ContextualToolbar>
      )}

      <DeleteEntitiesDialog
        isDeletionDialogOpen={isDeletionDialogOpen && !isReadOnly}
        onClose={() => setIsDeletionDialogOpen(false)}
      />
    </>
  );
}

type SelectAllConnectedScansProps = {
  /** The id of the scan to find the group for */
  selectedScanIds: GUID[];
};

function SelectAllConnectedScans({
  selectedScanIds,
}: SelectAllConnectedScansProps): JSX.Element {
  const { dispatch, getState } = useAppStore();

  const handleSelectConnected = useCallback(() => {
    const state = getState();

    const connectedCaptureIds = selectAllConnectedComponents(state)
      // Determine which groups are connected to the current selection
      // Connections are on the layer of scans instead of captures
      .filter((group) =>
        group.some((scanId) => selectedScanIds.includes(scanId)),
      )
      .flat()
      // Selection should be on captures instead of scans (for SiteScape)
      .map((scanId) => normalizeSelection(state, scanId));

    const deduplicatedCaptureIds = Array.from(new Set(connectedCaptureIds));

    Analytics.track<SelectedScanProperties>(EventType.selectConnectedScans, {
      selectedScanCount: deduplicatedCaptureIds.length,
    });

    dispatch(setSelectedEntityIds(deduplicatedCaptureIds));
  }, [dispatch, getState, selectedScanIds]);

  return (
    <FaroTooltip title="Select all connected captures">
      <FaroIconButton
        aria-label="select all connected captures"
        dark
        onClick={handleSelectConnected}
      >
        <SelectConnectedScansIcon />
      </FaroIconButton>
    </FaroTooltip>
  );
}

type ToggleFixedStatusForAllSelectedScansProps = {
  /** The ids of the scans that are currently selected */
  selectedScanIds: GUID[];
  /** Whether the current context is read-only */
  isReadOnly: boolean;
};

function ToggleFixedStatusForAllSelectedScans({
  selectedScanIds,
  isReadOnly,
}: ToggleFixedStatusForAllSelectedScansProps): JSX.Element {
  const [isProcessing, setIsProcessing] = useState(false);
  const dispatch = useAppDispatch();
  const { getState } = useAppStore();
  const { projectApiClient } = useApiClientContext();
  const fixedStatusCount = useFixedScanCount(selectedScanIds);
  const currentFixedStatus = useMemo(
    () => computeFixedStatus(fixedStatusCount, selectedScanIds.length),
    [fixedStatusCount, selectedScanIds.length],
  );

  const toggleFixedStatusForSelection = useCallback(async () => {
    if (isProcessing) return;

    Analytics.track<ToggleFixedStatusForSelectedScans>(
      EventType.toggleFixedStatusForSelectedScans,
      {
        via: "context toolbar",
        fixed: !currentFixedStatus,
        selectedScanCount: selectedScanIds.length,
      },
    );

    await toggleFixedStatus(
      dispatch,
      getState(),
      projectApiClient,
      currentFixedStatus,
      selectedScanIds,
      setIsProcessing,
    );
  }, [
    selectedScanIds,
    isProcessing,
    dispatch,
    getState,
    projectApiClient,
    currentFixedStatus,
  ]);

  return (
    <FaroTooltip
      title={
        currentFixedStatus
          ? "Release all selected captures"
          : "Fix all selected captures"
      }
    >
      <FaroIconButton
        dark
        aria-label={
          currentFixedStatus
            ? "release all selected captures"
            : "fix all selected captures"
        }
        disabled={isReadOnly || isProcessing || selectedScanIds.length === 0}
        onClick={toggleFixedStatusForSelection}
        showSpinner={isProcessing}
      >
        {currentFixedStatus ? (
          <ReleaseAllSelectedScansIcon />
        ) : (
          <FixAllSelectedScansIcon />
        )}
      </FaroIconButton>
    </FaroTooltip>
  );
}

type VisibilityToggleProps = {
  /**
   * The IDs of the directly selected entities.
   * Used to determine how the visibility should be toggled.
   */
  directlySelectedIds: GUID[];
  /**
   * The IDs of directly or indirectly selected entities.
   * The visibility for these entities will be changed.
   */
  selectedEntityIds: GUID[];
};

/**
 * @returns A button to toggle the visibility of the selected scans.
 *   If all selected scans are visible, it will hide them.
 *   If all selected scans are hidden, it will show them.
 *   If some are visible and some are hidden, it will toggle to the majority visibility.
 */
function VisibilityToggle({
  directlySelectedIds,
  selectedEntityIds,
}: VisibilityToggleProps): JSX.Element {
  const dispatch = useAppDispatch();

  const directlyVisibleCount = useVisibleCount(directlySelectedIds);

  const toggleToVisible = useMemo(() => {
    if (directlyVisibleCount === directlySelectedIds.length) {
      // All scans visible, toggle to hidden
      return false;
    } else if (directlyVisibleCount === 0) {
      // All scans hidden, toggle to visible
      return true;
    }

    // Toggle to the majority visibility
    return (
      directlyVisibleCount >= directlySelectedIds.length - directlyVisibleCount
    );
  }, [directlyVisibleCount, directlySelectedIds.length]);

  const toggleVisibility = useCallback(() => {
    Analytics.track<ToggleScanVisibilityProperties>(
      EventType.toggleScanVisibility,
      {
        via: "context toolbar",
        visible: toggleToVisible,
        origin: "staging area",
      },
    );
    // Set all selected scans to toggleToVisible value
    dispatch(
      setVisibilityForEntities({
        entityIds: selectedEntityIds,
        isVisible: toggleToVisible,
      }),
    );
  }, [selectedEntityIds, toggleToVisible, dispatch]);

  const title = useMemo(
    () =>
      toggleToVisible ? "Show selected captures" : "Hide selected captures",
    [toggleToVisible],
  );

  return (
    <FaroTooltip title={title}>
      <FaroIconButton aria-label={title} dark onClick={toggleVisibility}>
        {toggleToVisible ? <NonVisibleIcon /> : <VisibleIcon />}
      </FaroIconButton>
    </FaroTooltip>
  );
}

type FocusOnSelectionButtonProps = {
  /** The IDs of the selected scans. */
  selectedScanIds: GUID[];
};

/** @returns a button to focus the camera on the selected scans. */
function FocusOnSelectionButton({
  selectedScanIds,
}: FocusOnSelectionButtonProps): JSX.Element {
  const { getState } = useAppStore();
  const { centerCameraEvent } = useDataPreparationOverlayElements();

  const centerOnSelection = useCallback(() => {
    const state = getState();

    const scanPositions =
      selectRevisionEntityWorldPositions(selectedScanIds)(state);
    const bbox = new Box3()
      .setFromPoints(scanPositions)
      // Add a padding around the bounding box
      // Ensures that all important scan features are visible
      // Also fixes camera positioning bugs when the bounding box has 0 volume
      .expandByScalar(3);

    centerCameraEvent.emit({ bbox });
    Analytics.track<SelectedScanProperties>(EventType.centerOnSelection, {
      selectedScanCount: selectedScanIds.length,
    });
  }, [centerCameraEvent, selectedScanIds, getState]);

  return (
    <FaroTooltip title="Focus on selection">
      <FaroIconButton
        aria-label="focus on selection"
        dark
        onClick={centerOnSelection}
      >
        <FullScreenIcon
          sx={{ "& rect": { fill: "transparent" }, width: 30, height: 30 }}
        />
      </FaroIconButton>
    </FaroTooltip>
  );
}

/**
 * @param entityIds the IDs of the entities to check for visibility.
 * @returns how many of the given entities are currently visible.
 */
function useVisibleCount(entityIds: GUID[]): number {
  const visibilityMap = useAppSelector(selectVisibilityMap);

  return useMemo(
    () =>
      entityIds.reduce((visibleCount, entityId) => {
        if (visibilityMap[entityId] !== false) return visibleCount + 1;

        return visibleCount;
      }, 0),
    [visibilityMap, entityIds],
  );
}

/**
 * @param entityIds the IDs of the entities to check for fixed scans.
 * @returns how many of the given entities are currently fixed.
 */
export function useFixedScanCount(entityIds: GUID[]): number {
  const fixedScanIds = useAppSelector(selectFixedRevisionEntityIds);

  return useMemo(
    () =>
      entityIds.reduce((fixedCount, entityId) => {
        if (fixedScanIds.includes(entityId)) return fixedCount + 1;

        return fixedCount;
      }, 0),
    [fixedScanIds, entityIds],
  );
}
